(() => {
  const rootSelector = '[data-good-company-root]';
  const pauseDuration = 1500;

  function initialise(root) {
    if (root.dataset.goodCompanyReady === 'true') return;

    const gallery = root.querySelector('[data-good-company-marquee]');
    const track = root.querySelector('[data-good-company-track]');
    const group = root.querySelector('[data-good-company-group]');
    const previous = root.querySelector('[data-good-company-previous]');
    const next = root.querySelector('[data-good-company-next]');
    if (!gallery || !track || !group || !previous || !next) return;

    root.dataset.goodCompanyReady = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let resumeTimer;
    let pointerId = null;
    let touchId = null;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragAxis = null;
    let dragDistance = 0;
    let dragAnimation = null;
    let dragAnimationStart = 0;
    let dragAnimationDuration = 0;
    let dragGroupWidth = 0;
    let dragScrollStart = 0;

    const setTemporaryPause = () => {
      window.clearTimeout(resumeTimer);
      root.classList.add('is-user-paused');
      resumeTimer = window.setTimeout(() => {
        root.classList.remove('is-user-paused');
      }, pauseDuration);
    };

    const getAnimation = () => {
      if (typeof track.getAnimations !== 'function') return null;
      const animations = track.getAnimations();
      return animations.find((animation) => {
        const name = animation.animationName || '';
        return name === 'fg-good-company-marquee';
      }) || animations[0] || null;
    };

    const visibleItemDistance = (direction) => {
      const galleryRect = gallery.getBoundingClientRect();
      const items = Array.from(track.querySelectorAll('.good-company-gallery__item'))
        .map((item) => item.getBoundingClientRect())
        .sort((a, b) => a.left - b.left);
      const currentIndex = items.findIndex((rect) => rect.right > galleryRect.left + 2);
      if (currentIndex < 0) return gallery.clientWidth * 0.75;

      if (direction > 0 && items[currentIndex + 1]) {
        return Math.max(1, items[currentIndex + 1].left - items[currentIndex].left);
      }
      if (direction < 0 && items[currentIndex - 1]) {
        return Math.max(1, items[currentIndex].left - items[currentIndex - 1].left);
      }
      return Math.max(1, items[currentIndex].width + 18);
    };

    const fallbackScroll = (direction, distance) => {
      const groupWidth = group.getBoundingClientRect().width;
      if (direction > 0 && gallery.scrollLeft >= groupWidth) gallery.scrollLeft -= groupWidth;
      if (direction < 0 && gallery.scrollLeft <= 1) gallery.scrollLeft = groupWidth;
      gallery.scrollBy({ left: direction * distance, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    const step = (direction) => {
      setTemporaryPause();
      const distance = visibleItemDistance(direction);
      const animation = getAnimation();
      const duration = Number(animation?.effect?.getTiming().duration);
      const groupWidth = group.getBoundingClientRect().width;

      if (!reducedMotion.matches && animation && Number.isFinite(duration) && duration > 0 && groupWidth > 0) {
        const currentTime = Number(animation.currentTime) || 0;
        const timeDelta = (distance / groupWidth) * duration;
        animation.currentTime = (currentTime + direction * timeDelta + duration) % duration;
        return;
      }
      fallbackScroll(direction, distance);
    };

    const beginDrag = (clientX, clientY) => {
      window.clearTimeout(resumeTimer);
      root.classList.add('is-pointer-down', 'is-user-paused');
      dragStartX = clientX;
      dragStartY = clientY;
      dragAxis = null;
      dragDistance = 0;
      dragAnimation = getAnimation();
      dragAnimationStart = Number(dragAnimation?.currentTime) || 0;
      dragAnimationDuration = Number(dragAnimation?.effect?.getTiming().duration) || 0;
      dragGroupWidth = group.getBoundingClientRect().width;
      dragScrollStart = gallery.scrollLeft;
    };

    const moveDrag = (clientX, clientY, event) => {
      const deltaX = clientX - dragStartX;
      const deltaY = clientY - dragStartY;
      if (!dragAxis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 5) {
        dragAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
      }
      if (dragAxis !== 'x') return;
      event.preventDefault();
      dragDistance = Math.max(dragDistance, Math.abs(deltaX));

      if (!reducedMotion.matches && dragAnimation && dragAnimationDuration > 0 && dragGroupWidth > 0) {
        const timeDelta = (-deltaX / dragGroupWidth) * dragAnimationDuration;
        dragAnimation.currentTime = (dragAnimationStart + timeDelta + dragAnimationDuration) % dragAnimationDuration;
      } else {
        gallery.scrollLeft = dragScrollStart - deltaX;
      }
    };

    const endDrag = () => {
      if (!root.classList.contains('is-pointer-down')) return;
      root.classList.remove('is-pointer-down');
      dragAxis = null;
      dragAnimation = null;
      setTemporaryPause();
    };

    previous.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      step(event.key === 'ArrowRight' ? 1 : -1);
    });

    gallery.addEventListener('wheel', (event) => {
      const horizontalDelta = Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : (event.shiftKey ? event.deltaY : 0);
      if (!horizontalDelta) return;
      event.preventDefault();
      setTemporaryPause();
      const animation = getAnimation();
      const duration = Number(animation?.effect?.getTiming().duration);
      const groupWidth = group.getBoundingClientRect().width;
      if (!reducedMotion.matches && animation && Number.isFinite(duration) && duration > 0 && groupWidth > 0) {
        const currentTime = Number(animation.currentTime) || 0;
        animation.currentTime = (currentTime + ((horizontalDelta / groupWidth) * duration) + duration) % duration;
      } else {
        gallery.scrollLeft += horizontalDelta;
      }
    }, { passive: false });

    gallery.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' && 'ontouchstart' in window) return;
      if (event.button !== undefined && event.button !== 0) return;
      pointerId = event.pointerId;
      beginDrag(event.clientX, event.clientY);
      gallery.setPointerCapture?.(pointerId);
    });
    gallery.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      moveDrag(event.clientX, event.clientY, event);
    }, { passive: false });
    const releasePointer = (event) => {
      if (event.pointerId !== pointerId) return;
      gallery.releasePointerCapture?.(pointerId);
      pointerId = null;
      endDrag();
    };
    gallery.addEventListener('pointerup', releasePointer);
    gallery.addEventListener('pointercancel', releasePointer);
    gallery.addEventListener('lostpointercapture', (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      endDrag();
    });

    const touchById = (touches) => {
      for (let index = 0; index < touches.length; index += 1) {
        if (touches[index].identifier === touchId) return touches[index];
      }
      return null;
    };
    gallery.addEventListener('touchstart', (event) => {
      if (touchId !== null || event.touches.length !== 1) return;
      const touch = event.changedTouches[0];
      touchId = touch.identifier;
      beginDrag(touch.clientX, touch.clientY);
    }, { passive: true });
    gallery.addEventListener('touchmove', (event) => {
      if (touchId === null) return;
      const touch = touchById(event.touches);
      if (!touch) return;
      moveDrag(touch.clientX, touch.clientY, event);
    }, { passive: false });
    const releaseTouch = (event) => {
      if (touchId === null || !touchById(event.changedTouches)) return;
      touchId = null;
      endDrag();
    };
    gallery.addEventListener('touchend', releaseTouch, { passive: true });
    gallery.addEventListener('touchcancel', releaseTouch, { passive: true });
    gallery.addEventListener('dragstart', (event) => event.preventDefault());

    gallery.addEventListener('click', (event) => {
      if (dragDistance <= 6) return;
      event.preventDefault();
      event.stopPropagation();
      dragDistance = 0;
    }, true);
  }

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
